// Data-heavy SEO intent registry; exported through seo-intents.ts for stable imports.

import {
  SEO_PRIMARY_REPORT_OFFERS,
  SEO_REPORT_PLAN_IDS,
  type GovernedSEOReportIntent,
  type GovernedSEOReportPlanId,
} from "./seo-primary-offers.generated";
import {
  customerAstrologerPath,
  customerKundaliPath,
  customerLanguageFromPath,
  type CustomerLanguage,
} from "./customer-language";

export type SEOIntent =
  | "daily_guidance"
  | "timing_muhurat"
  | "chart_report"
  | "remedy_dosha"
  | "festival_ritual"
  | "generic";

export type SEOOfferType = "report" | "product" | "puja";

export type SEOTarget = "free-kundali" | "ai-astrologer";

export type SEOReportPlanId = GovernedSEOReportPlanId;

/** Generated from the governed primary-report projection; never hand-copy plan IDs here. */
export const PLAN_ID = SEO_REPORT_PLAN_IDS;

/**
 * Narrowing guard for the PRIMARY report path. The ₹49 micro-consult SKU
 * (`ask_one_question`) widens the CTA boundary's `ReportPlanId`, but it is a
 * SECONDARY-only SKU and never a
 * primary report plan — it has no premium-report copy in `REPORT_COPY`. Use this
 * to keep an arbitrary routed plan id from reaching the `SEOReportPlanId`-keyed
 * copy lookup, so the primary path stays type-sound and never resolves to the
 * micro-consult SKU.
 */
export function isSEOReportPlanId(value: unknown): value is SEOReportPlanId {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(PLAN_ID, value);
}

function governedReportPlanId(intent: GovernedSEOReportIntent): SEOReportPlanId {
  return SEO_PRIMARY_REPORT_OFFERS[intent];
}

export interface SEOIntentConfig {
  intent: SEOIntent;
  pageCategory: string;
  eyebrow: string;
  headline: string;
  subhead: string;
  prompt: string;
  ctaLabel: string;
  target: SEOTarget;
  primaryOffer: SEOOfferType;
  reportPlanId: SEOReportPlanId;
  starterPrompts: string[];
  previewTitle: string;
  previewItems: string[];
}

type SEOIntentCopy = Pick<
  SEOIntentConfig,
  | "eyebrow"
  | "headline"
  | "subhead"
  | "prompt"
  | "ctaLabel"
  | "starterPrompts"
  | "previewTitle"
  | "previewItems"
>;

const INTENT_CONFIGS: Record<SEOIntent, SEOIntentConfig> = {
  daily_guidance: {
    intent: "daily_guidance",
    pageCategory: "rashifal",
    eyebrow: "Personalized AI Jyotish",
    headline: "Why does today's rashifal feel half-true?",
    subhead:
      "Your Moon sign shows the mood. Your kundali shows why this phase is repeating, and which upay fits today.",
    prompt: "What does today's rashifal mean for my kundali?",
    ctaLabel: "Read my kundali",
    target: "free-kundali",
    primaryOffer: "report",
    reportPlanId: governedReportPlanId("forecast"),
    starterPrompts: [
      "What does today's rashifal mean for my kundali?",
      "Why does this week feel stuck even when I am trying?",
      "Which planet is affecting my career or relationship decisions right now?",
    ],
    previewTitle: "What your annual bhavishya will show",
    previewItems: [
      "Why today's rashifal is repeating in your dasha.",
      "Which months support career, money, and relationship decisions.",
      "Which upay fits the year before the pressure builds.",
    ],
  },
  timing_muhurat: {
    intent: "timing_muhurat",
    pageCategory: "muhurat",
    eyebrow: "Personal Muhurat Assistant",
    headline: "Why does the same choghadiya not work for everyone?",
    subhead:
      "A good muhurat depends on your city, purpose, Moon sign, and active dasha. Get the timing and sankalp together.",
    prompt: "Which muhurat is right for my exact purpose?",
    ctaLabel: "Get my muhurat",
    target: "free-kundali",
    primaryOffer: "report",
    reportPlanId: governedReportPlanId("timing"),
    starterPrompts: [
      "Which time is best for my specific work?",
      "Should I avoid Rahu Kaal for this decision?",
      "What sankalp should I take before starting?",
    ],
    previewTitle: "What your personal muhurat plan will show",
    previewItems: [
      "Why the same choghadiya does not work for everyone.",
      "How your city, Moon sign, dasha, and purpose decide the better window.",
      "Which timing to avoid and what sankalp to take before starting.",
    ],
  },
  chart_report: {
    intent: "chart_report",
    pageCategory: "kundali",
    eyebrow: "AI Kundali Analysis",
    headline: "Why does this pattern keep repeating in your life?",
    subhead:
      "Your kundali names the house, planet, and dasha behind it. Then it gives the upay you can actually follow.",
    prompt: "Which pattern in my kundali is affecting me the most?",
    ctaLabel: "Generate my kundali",
    target: "free-kundali",
    primaryOffer: "report",
    reportPlanId: governedReportPlanId("birth_chart"),
    starterPrompts: [
      "Why does this same problem keep coming back?",
      "Which dasha is active and what does it mean?",
      "Which upay fits my chart right now?",
    ],
    previewTitle: "What your kundali report will show",
    previewItems: [
      "Which house and planet explain the pattern you keep seeing.",
      "How your active dasha changes the timing of career, marriage, and money.",
      "Which upay fits your chart before you guess or overspend.",
    ],
  },
  remedy_dosha: {
    intent: "remedy_dosha",
    pageCategory: "dosha",
    eyebrow: "Dosha Diagnosis",
    headline: "Why follow a dosha upay before checking your chart?",
    subhead:
      "Kaal Sarp Dosh, Pitra Dosh, Mangal Dosh, and Sade Sati act differently in every kundali. Confirm the strength, then choose the upay.",
    prompt: "Do I really have this dosha, and which upay fits my chart?",
    ctaLabel: "Check my dosha",
    target: "free-kundali",
    primaryOffer: "product",
    reportPlanId: governedReportPlanId("dosha_remedy"),
    starterPrompts: [
      "Do I really have this dosha?",
      "Which remedy fits my chart?",
      "Should I do mantra, product upay, or shanti?",
    ],
    previewTitle: "What your dosha remedy plan will show",
    previewItems: [
      "Do you really have Kaal Sarp Dosh, Pitra Dosh, Mangal Dosh, or Sade Sati?",
      "How strong it is in your kundali, and whether cancellation applies.",
      "Which upay is safe for your chart, and when a product or shanti is actually needed.",
    ],
  },
  festival_ritual: {
    intent: "festival_ritual",
    pageCategory: "festival",
    eyebrow: "Festival Puja Guide",
    headline: "Why should your festival puja be the same as everyone else's?",
    subhead:
      "Your kundali shows the sankalp, timing, and upay that make the ritual personal instead of just another checklist.",
    prompt: "What sankalp and timing fit my kundali for this puja?",
    ctaLabel: "Get my sankalp",
    target: "free-kundali",
    primaryOffer: "report",
    reportPlanId: governedReportPlanId("festival_ritual"),
    starterPrompts: [
      "What sankalp should I take for this festival?",
      "Which timing is best for my city?",
      "Which remedy or puja item fits my chart?",
    ],
    previewTitle: "What your sankalp plan will show",
    previewItems: [
      "Why your festival puja should follow your kundali, not only a generic checklist.",
      "Which muhurat, sankalp, and avoid windows fit your city and chart.",
      "Which upay or puja item supports the ritual without overdoing it.",
    ],
  },
  generic: {
    intent: "generic",
    pageCategory: "generic",
    // The card links to the kundali form (target below), so the label names
    // the kundali. "Free AI Astrologer" named the /ai-astrologer/ chat product
    // while ~60 pages sent the click to /free-kundali/.
    eyebrow: "Free Kundali Reading",
    headline: "Why does your chart keep pointing to the same answer?",
    subhead:
      "Ask with your kundali, not a guess. See the planet behind the problem and the upay that fits your life.",
    prompt: "Read my kundali and tell me the truth about this pattern.",
    ctaLabel: "Ask my chart",
    target: "free-kundali",
    primaryOffer: "report",
    reportPlanId: governedReportPlanId("birth_chart"),
    starterPrompts: [
      "Why does this keep happening to me?",
      "Which planet is strongest in my chart right now?",
      "Which remedy is right for me?",
    ],
    previewTitle: "What your chart will show",
    previewItems: [
      "Which planetary pattern explains what keeps repeating.",
      "Which dasha or dosha is most active right now.",
      "Which upay gives you the next step instead of another guess.",
    ],
  },
};

/**
 * Hindi-path interaction copy uses the brand's natural Hinglish register
 * (rule 21). It lives beside the canonical intent registry so shared CTA,
 * starter-prompt, and preview surfaces cannot drift into English-only UI.
 */
const HI_INTENT_COPY: Record<SEOIntent, SEOIntentCopy> = {
  daily_guidance: {
    eyebrow: "Personal AI Jyotish",
    headline: "Aaj ka rashifal aadha-sach kyun lagta hai?",
    subhead:
      "Moon sign mood batata hai. Aapki kundali batati hai ki yeh phase kyun repeat ho raha hai aur aaj kaunsa upay sahi hai.",
    prompt: "Aaj ka rashifal meri kundali ke liye kya kehta hai?",
    ctaLabel: "Meri kundali padhein",
    starterPrompts: [
      "Aaj ka rashifal meri kundali ke liye kya kehta hai?",
      "Koshish ke baad bhi yeh hafta stuck kyun lag raha hai?",
      "Abhi kaunsa graha career ya relationship decisions par asar daal raha hai?",
    ],
    previewTitle: "Aapka annual bhavishya kya dikhayega",
    previewItems: [
      "Aaj ka rashifal aapki dasha mein kyun repeat ho raha hai.",
      "Career, paisa aur relationship decisions ke liye kaunse months support karte hain.",
      "Pressure badhne se pehle saal ke liye kaunsa upay sahi hai.",
    ],
  },
  timing_muhurat: {
    eyebrow: "Personal Muhurat Guide",
    headline: "Ek hi choghadiya sabke liye kaam kyun nahi karta?",
    subhead:
      "Sahi muhurat aapke city, purpose, Moon sign aur active dasha par depend karta hai. Timing aur sankalp saath dekhein.",
    prompt: "Mere exact purpose ke liye kaunsa muhurat sahi hai?",
    ctaLabel: "Mera muhurat dekhein",
    starterPrompts: [
      "Mere kaam ke liye kaunsa time best hai?",
      "Kya is decision ke liye Rahu Kaal avoid karna chahiye?",
      "Shuru karne se pehle kaunsa sankalp lena chahiye?",
    ],
    previewTitle: "Aapka personal muhurat plan kya dikhayega",
    previewItems: [
      "Ek hi choghadiya sabke liye kyun kaam nahi karta.",
      "City, Moon sign, dasha aur purpose milkar better window kaise decide karte hain.",
      "Kaunsi timing avoid karein aur shuru karne se pehle kaunsa sankalp lein.",
    ],
  },
  chart_report: {
    eyebrow: "AI Kundali Analysis",
    headline: "Aapki life mein wahi pattern baar-baar kyun aa raha hai?",
    subhead:
      "Kundali uske peeche ka house, graha aur dasha batati hai — phir woh upay jo aap sach mein follow kar sakte hain.",
    prompt: "Meri kundali ka kaunsa pattern mujhe sabse zyada affect kar raha hai?",
    ctaLabel: "Meri kundali banayein",
    starterPrompts: [
      "Wahi problem baar-baar kyun aa rahi hai?",
      "Abhi kaunsi dasha active hai aur uska kya matlab hai?",
      "Meri kundali ke liye abhi kaunsa upay sahi hai?",
    ],
    previewTitle: "Aapki kundali report kya dikhayegi",
    previewItems: [
      "Kaunsa house aur graha repeat hone wale pattern ko explain karta hai.",
      "Active dasha career, shaadi aur paise ki timing kaise badalti hai.",
      "Guess ya overspend karne se pehle kaunsa upay sahi hai.",
    ],
  },
  remedy_dosha: {
    eyebrow: "Dosha Diagnosis",
    headline: "Kundali check kiye bina dosha upay kyun follow karein?",
    subhead:
      "Kaal Sarp Dosh, Pitra Dosh, Mangal Dosh aur Sade Sati har kundali mein alag kaam karte hain. Pehle strength confirm karein, phir upay chunein.",
    prompt: "Kya mujhe sach mein yeh dosha hai aur meri kundali ke liye kaunsa upay sahi hai?",
    ctaLabel: "Mera dosha check karein",
    starterPrompts: [
      "Kya mujhe sach mein yeh dosha hai?",
      "Meri kundali ke liye kaunsa upay sahi hai?",
      "Mantra, product upay ya shanti — mujhe kya karna chahiye?",
    ],
    previewTitle: "Aapka dosha upay plan kya dikhayega",
    previewItems: [
      "Kaal Sarp Dosh, Pitra Dosh, Mangal Dosh ya Sade Sati sach mein hai ya nahi.",
      "Kundali mein dosha kitna strong hai aur cancellation lagti hai ya nahi.",
      "Kaunsa upay safe hai aur product ya shanti ki zaroorat kab hai.",
    ],
  },
  festival_ritual: {
    eyebrow: "Festival Puja Guide",
    headline: "Aapki festival puja sabke jaisi kyun honi chahiye?",
    subhead:
      "Aapki kundali woh sankalp, timing aur upay batati hai jo ritual ko generic checklist ke bajaye personal banata hai.",
    prompt: "Is puja ke liye meri kundali ke hisaab se kaunsa sankalp aur time sahi hai?",
    ctaLabel: "Mera sankalp dekhein",
    starterPrompts: [
      "Is festival ke liye mujhe kaunsa sankalp lena chahiye?",
      "Mere city ke liye kaunsi timing best hai?",
      "Meri kundali ke liye kaunsa upay ya puja item sahi hai?",
    ],
    previewTitle: "Aapka sankalp plan kya dikhayega",
    previewItems: [
      "Festival puja generic checklist ke bajaye kundali ko kyun follow kare.",
      "Aapke city aur kundali ke liye kaunsa muhurat, sankalp aur avoid window sahi hai.",
      "Ritual ko overdo kiye bina kaunsa upay ya puja item support karega.",
    ],
  },
  generic: {
    eyebrow: "Free Kundali Reading",
    headline: "Aapki kundali baar-baar wahi answer kyun dikha rahi hai?",
    subhead:
      "Guess se nahi, apni kundali se poochhein. Problem ke peeche ka graha aur life ke liye sahi upay dekhein.",
    prompt: "Meri kundali padhkar is pattern ka sach batayein.",
    ctaLabel: "Meri kundali se poochhein",
    starterPrompts: [
      "Mere saath yeh baar-baar kyun ho raha hai?",
      "Abhi meri kundali mein kaunsa graha sabse strong hai?",
      "Mere liye kaunsa upay sahi hai?",
    ],
    previewTitle: "Aapki kundali kya dikhayegi",
    previewItems: [
      "Kaunsa graha pattern repeat hone ki wajah batata hai.",
      "Abhi kaunsi dasha ya dosha sabse active hai.",
      "Ek aur guess ke bajaye kaunsa upay agla step deta hai.",
    ],
  },
};

/**
 * Prashna is the one surface that deliberately keeps the `generic` intent id (see
 * the source override in `getSEOIntentConfigForSource`), so the generic Hindi copy
 * would otherwise overwrite it with kundali framing — re-breaking the very "no
 * birth details" promise in Hindi that the override exists to honour.
 */
const PRASHNA_HI_COPY: SEOIntentCopy = {
  eyebrow: "Prashna Follow-up",
  headline: "Chart ne yeh sawal to bata diya. Ab agla sawal kya hai?",
  subhead:
    "Prashna us pal ko padhta hai jab aapne poochha. Wahi baat Pandit-ji ke saath aage badhayein, dobara shuru karne ke bajaye.",
  prompt: "Mere prashna ka jawab ek taraf ishara kar raha hai — ab mujhe kya karna chahiye?",
  ctaLabel: "Agla sawal poochhein",
  starterPrompts: [
    "Abhi jo sawal poochha, uspar aage kya karna chahiye?",
    "Prashna chart isi taraf kyun jhuka?",
    "Yeh jawab kitni jaldi lagoo hota hai?",
  ],
  previewTitle: "Follow-up baat mein kya cover hoga",
  previewItems: [
    "Aapke prashna chart ke ruling grah asal mein kis taraf ishara kar rahe the.",
    "Jawab ka kaunsa hissa timing hai aur kaunsa aapka decision.",
    "Agla practical step, aapke apne shabdon mein poochha aur samjhaya gaya.",
  ],
};

export function localizeSEOIntentConfig(
  config: SEOIntentConfig,
  language: CustomerLanguage
): SEOIntentConfig {
  if (language !== "hi") return config;
  const copy = config.pageCategory === "prashna" ? PRASHNA_HI_COPY : HI_INTENT_COPY[config.intent];
  return { ...config, ...copy };
}

const DIRECT_SLUG_INTENTS: Record<string, SEOIntent> = {
  "aaj-ka-rashifal": "daily_guidance",
  rashifal: "daily_guidance",
  "weekly-rashifal": "daily_guidance",
  "monthly-rashifal": "daily_guidance",
  panchang: "timing_muhurat",
  choghadiya: "timing_muhurat",
  "tithi-today": "timing_muhurat",
  "rahu-kaal": "timing_muhurat",
  muhurat: "timing_muhurat",
  "shubh-muhurat": "timing_muhurat",
  "free-kundali": "chart_report",
  "ai-kundli": "chart_report",
  "birth-chart": "chart_report",
  "kundli-matching": "chart_report",
  "gun-milan": "chart_report",
  compatibility: "chart_report",
  "rashi-calculator": "chart_report",
  "nakshatra-calculator": "chart_report",
  shadbala: "chart_report",
  "navamsa-chart": "chart_report",
  "mangal-dosha-calculator": "remedy_dosha",
  "manglik-calculator": "remedy_dosha",
  "pitra-dosha": "remedy_dosha",
  "kaal-sarp-dosha": "remedy_dosha",
  "sade-sati": "remedy_dosha",
  "sade-sati-report": "remedy_dosha",
  "pitra-dosh-report": "remedy_dosha",
  "dosha-remedy-plan": "remedy_dosha",
  "dosha-report": "remedy_dosha",
  "mahadasha-forecast": "chart_report",
  "career-astrology-report": "chart_report",
  "career-report": "chart_report",
  "marriage-report": "chart_report",
  "muhurat-report": "timing_muhurat",
  remedies: "remedy_dosha",
  upay: "remedy_dosha",
  // Vrat hubs — festival/puja-ritual intent (sankalp + timing + puja upay).
  ekadashi: "festival_ritual",
  amavasya: "festival_ritual",
  purnima: "festival_ritual",
  pradosh: "festival_ritual",
  // Sankashti Chaturthi is the obstacle-removal (Vighnaharta) vrat — its value
  // moment is "why does my work keep getting blocked?", so it leads to the dosha
  // remedy offer, not the generic festival sankalp report (rule 40).
  "sankashti-chaturthi": "remedy_dosha",
  // Masik Shivratri is the monthly Shiva vrat — its value moment is "why does
  // the same heaviness/bad luck keep returning?" (Shani/Mangal), so it leads to
  // the dosha remedy offer like the Shani surfaces (rule 40).
  "masik-shivratri": "remedy_dosha",
  // Sankranti (12 solar ingresses) + the vrat-calendar hub are festival/ritual
  // surfaces — the value moment is the sankalp + timing + puja upay.
  sankranti: "festival_ritual",
  "vrat-calendar": "festival_ritual",
};

const FESTIVAL_SLUG_HINTS = [
  "diwali",
  "dhanteras",
  "navratri",
  "janmashtami",
  "ganesh-chaturthi",
  "karwa-chauth",
  "raksha-bandhan",
  "bhai-dooj",
  "govardhan-puja",
  "dussehra",
  "holi",
  "chhath-puja",
];

export function normalizeSEOIntent(intent?: string | null): SEOIntent {
  if (!intent) return "generic";
  return intent in INTENT_CONFIGS ? (intent as SEOIntent) : "generic";
}

export function getSEOIntentConfig(intent?: string | null): SEOIntentConfig {
  return INTENT_CONFIGS[normalizeSEOIntent(intent)];
}

export function inferSEOIntentFromSlug(slug?: string | null): SEOIntent {
  if (!slug) return "generic";
  const clean = slug.replace(/^\/+|\/+$/g, "").replace(/^hi\//, "");
  const first = clean.split("/")[0] ?? "";

  if (DIRECT_SLUG_INTENTS[clean]) return DIRECT_SLUG_INTENTS[clean];
  if (DIRECT_SLUG_INTENTS[first]) return DIRECT_SLUG_INTENTS[first];
  if (FESTIVAL_SLUG_HINTS.some((hint) => clean.includes(hint))) return "festival_ritual";
  if (clean.includes("dosha") || clean.includes("dosh")) return "remedy_dosha";
  if (clean.includes("transit") || clean.includes("mahadasha")) return "chart_report";
  return "generic";
}

export function getSEOIntentConfigForSlug(slug?: string | null): SEOIntentConfig {
  return getSEOIntentConfig(inferSEOIntentFromSlug(slug));
}

export function getSEOIntentConfigForSource(slug?: string | null): SEOIntentConfig {
  const config = getSEOIntentConfigForSlug(slug);
  const clean = (slug || "").toLowerCase();

  // Prashna Kundali is HORARY: the chart is cast for the moment of the question,
  // and the page earns its click by promising "no birth time, no birth date".
  // Sending that seeker to the birth-details form contradicts the promise that
  // earned the click (rules 40/43), so every prashna surface targets the chat —
  // the one place where the next step is still just a question. Checked first
  // because no later override may ever hand this surface back to a form.
  // The override deliberately keeps `config.intent` (like the career/marriage
  // branches): SEOAgentBridge only honours a source override when its intent
  // equals the intent the caller passed, so a bespoke intent id would be
  // silently discarded.
  if (clean.includes("prashna")) {
    return {
      ...config,
      pageCategory: "prashna",
      eyebrow: "Prashna Follow-up",
      headline: "The chart answered this question. What about the next one?",
      subhead:
        "Prashna reads the moment you asked. Keep the same thread going with Pandit-ji instead of starting over somewhere else.",
      prompt: "My prashna answer pointed one way — what should I actually do next?",
      ctaLabel: "Ask the next question",
      target: "ai-astrologer",
      starterPrompts: [
        "What should I do next about the question I just asked?",
        "Why did the prashna chart lean this way?",
        "How soon does this answer apply?",
      ],
      previewTitle: "What the follow-up conversation covers",
      previewItems: [
        "What the ruling planets of your prashna chart were actually pointing at.",
        "Which part of the answer is timing and which part is your decision.",
        "The next practical step, asked and answered in your own words.",
      ],
    };
  }

  // Career pages must sell the Career & Finance report, not the generic Brihat
  // (intent_offer_alignment.yaml: career -> career_report). "business" is too
  // collision-prone (muhurat/business) — match specific career terms only.
  // "dasamsa" (the /dasamsa + /hi/dasamsa D10 calculator) IS a career surface:
  // D10/Dasamsa is the career divisional chart, and career_report explicitly
  // covers "10th house and D10/Dasamsa career direction" (report-intent-data).
  const isCareerPath = ["career", "job", "profession", "naukri", "dasamsa"].some((hint) =>
    clean.includes(hint)
  );
  if (isCareerPath) {
    return {
      ...config,
      pageCategory: "career",
      eyebrow: "Career & Finance Report",
      headline: "What's really blocking your career growth?",
      subhead:
        "See your 10th house, current dasha, Saturn/Jupiter transits, and the specific upays for career momentum — built from your chart, not generic advice.",
      prompt: "Why isn't my career growing the way it should?",
      ctaLabel: "Start career report",
      target: "free-kundali",
      primaryOffer: "report",
      reportPlanId: governedReportPlanId("career"),
      starterPrompts: [
        "Why does my hard work not turn into promotions or income?",
        "Is now the right time for a job change or a new business?",
        "Which upays strengthen my 10th house and career yoga?",
      ],
      previewTitle: "What your career report will show",
      previewItems: [
        "10th house, D10/Dasamsa, and career-yoga read for your chart.",
        "Current dasha + Saturn/Jupiter transit timing for career moves.",
        "Specific upays and a practical next-step plan for momentum.",
      ],
    };
  }

  const specificReport = specificReportConfig(clean, config);
  if (specificReport) return specificReport;

  const isMarriagePath = [
    "marriage",
    "kundli-matching",
    "gun-milan",
    "compatibility",
    "manglik",
    "navamsa",
  ].some((hint) => clean.includes(hint));

  if (!isMarriagePath) return config;

  return {
    ...config,
    pageCategory: "marriage",
    eyebrow: "Marriage Compatibility Report",
    headline: "Check the match beyond a basic score",
    subhead:
      "Use both birth details to see Gun Milan, Mangal Dosha, D9/Navamsha, dasha support, timing, and practical remedies before families decide.",
    prompt: "What should we check before deciding this marriage match?",
    ctaLabel: "Start matching report",
    target: "free-kundali",
    primaryOffer: "report",
    reportPlanId: governedReportPlanId("matching"),
    starterPrompts: [
      "Is this match safe beyond the Gun Milan score?",
      "Do Mangal Dosha, Nadi, Bhakoot, or dasha timing create risk?",
      "What remedies or timing should we consider before moving ahead?",
    ],
    previewTitle: "What your marriage report will show",
    previewItems: [
      "Full 36-point Gun Milan plus Mangal Dosha and major dosha cross-check.",
      "D9/Navamsha and dasha timing signals for marriage stability.",
      "Clear next steps: strengths, risks, remedies, and family-shareable summary.",
    ],
  };
}

type SpecificReportOverride = Partial<Omit<SEOIntentConfig, "intent">> &
  Pick<SEOIntentConfig, "pageCategory" | "reportPlanId">;

const SPECIFIC_REPORT_OVERRIDES: {
  matches: string[];
  config: SpecificReportOverride;
}[] = [
  {
    matches: ["pitra-dosh", "pitra-dosha"],
    config: {
      pageCategory: "pitra-dosh",
      eyebrow: "Pitra Dosh Report",
      headline: "Is ancestral karma blocking your family or career path?",
      subhead:
        "Confirm Pitra Dosh from the 9th house, Sun, Rahu/Ketu, cancellation factors, and the upay sequence matched to your kundali.",
      prompt: "Do I have Pitra Dosh, and which upay fits my chart?",
      ctaLabel: "Start Pitra Dosh report",
      target: "free-kundali",
      primaryOffer: "report",
      reportPlanId: governedReportPlanId("pitra_dosh_report"),
      starterPrompts: [
        "Do I really have Pitra Dosh?",
        "Which ancestor-related pattern is active in my chart?",
        "Should I do tarpan, shradh, mantra, or another upay?",
      ],
      previewTitle: "What your Pitra Dosh report will show",
      previewItems: [
        "Whether Pitra Dosh is actually present or cancelled.",
        "How the 9th house, Sun, Rahu/Ketu, and dasha activate the pattern.",
        "A safe upay sequence before you spend on unnecessary rituals.",
      ],
    },
  },
  {
    matches: ["sade-sati", "shani"],
    config: {
      pageCategory: "sade-sati",
      eyebrow: "Sade Sati Report",
      headline: "Which Sade Sati phase is testing your Moon sign now?",
      subhead:
        "Read Shani over your Moon sign with natal Saturn, dasha context, pressure areas, and chart-specific upay.",
      prompt: "Which Sade Sati phase am I in, and what should I do?",
      ctaLabel: "Start Sade Sati report",
      target: "free-kundali",
      primaryOffer: "report",
      reportPlanId: governedReportPlanId("sade_sati_report"),
      starterPrompts: [
        "Which Sade Sati phase am I in?",
        "How is Shani affecting my career and family right now?",
        "Which Shani upay fits my kundali?",
      ],
      previewTitle: "What your Sade Sati report will show",
      previewItems: [
        "Your current Sade Sati phase and pressure areas.",
        "How natal Shani and current dasha change the result.",
        "Which Shani upay to follow without fear-based overdoing.",
      ],
    },
  },
  {
    matches: ["mahadasha", "dasha-calculator"],
    config: {
      pageCategory: "mahadasha",
      eyebrow: "Mahadasha Forecast",
      headline: "Which mahadasha is quietly setting the timing of your life?",
      subhead:
        "See your current mahadasha, antardasha, next transitions, and the next 12 months of practical timing.",
      prompt: "Which mahadasha is active and what will change next?",
      ctaLabel: "Start Mahadasha forecast",
      target: "free-kundali",
      primaryOffer: "report",
      reportPlanId: governedReportPlanId("mahadasha_forecast"),
      starterPrompts: [
        "Which mahadasha is active in my chart?",
        "What will the next antardasha change?",
        "Which months support career, money, or marriage decisions?",
      ],
      previewTitle: "What your Mahadasha forecast will show",
      previewItems: [
        "Current mahadasha and antardasha interpreted from your Moon nakshatra.",
        "Next 12 months of sub-periods and transition points.",
        "Upay and decision windows matched to the graha period.",
      ],
    },
  },
  {
    matches: ["dosha-remedy", "dosha-report"],
    config: {
      reportPlanId: governedReportPlanId("dosha_remedy"),
      pageCategory: "dosha-remedy",
    },
  },
  {
    matches: ["muhurat"],
    config: {
      reportPlanId: governedReportPlanId("timing"),
      pageCategory: "muhurat",
    },
  },
];

function specificReportConfig(cleanPath: string, config: SEOIntentConfig): SEOIntentConfig | null {
  const match = SPECIFIC_REPORT_OVERRIDES.find((entry) =>
    entry.matches.some((token) => cleanPath.includes(token))
  );
  return match ? { ...config, ...match.config } : null;
}

export function buildSEOAgentHref(
  config: SEOIntentConfig,
  sourcePath?: string | null,
  language = customerLanguageFromPath(sourcePath)
): string {
  const base =
    config.target === "ai-astrologer"
      ? customerAstrologerPath(language)
      : customerKundaliPath(language);
  // A bridge on the kundali page itself used to link here with an #intent=
  // hash, which a same-page tap ignores (it is read on mount only): no scroll,
  // no reload, the form stayed off-screen (C25). Land it on the form instead.
  if (config.target !== "ai-astrologer" && sourcePath?.replace(/\/+$/, "") === base) {
    return `${base}/#${language === "hi" ? "kundali-form-hi" : "kundali-form"}`;
  }
  const params = new URLSearchParams({
    intent: config.intent,
    source: sourcePath || config.pageCategory,
  });
  return `${base}/#${params.toString()}`;
}
